import React, { useEffect, useState } from 'react';
import { LuX, LuFileText } from 'react-icons/lu';
import {doc, addDoc, collection, getDocs, setDoc} from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useOrganization } from "../../../context/OrgContext";

const NewInterfaceModal = ({ isOpen, onClose, onInterfaceAdded, pathSegments }) => {
  const { institution } = useOrganization();
  const [interfaceTitle, setInterfaceTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // Build collection path from pathSegments
  const buildCollectionPath = () => {
    if (!institution?.id || !pathSegments) return null;
    
    let collectionPath = `colleges/${institution.id}`;
    
    // Check if we're creating an interface in a subsection (odd number of path segments >= 3)
    if (pathSegments.length >= 3 && pathSegments.length % 2 === 1) {
      
      // Build path to parent section
      const parentSegments = pathSegments.slice(0, -1); // Remove subsection
      const parentInterfaceSlug = parentSegments[parentSegments.length - 1];
      const grandparentSegments = parentSegments.slice(0, -1);
      
      for (let i = 0; i < grandparentSegments.length; i += 2) {
        collectionPath += `/${grandparentSegments[i]}.section`;
        if (i + 1 < grandparentSegments.length) {
          collectionPath += `/${grandparentSegments[i + 1]}`;
        }
      }
      console.log("ColPath ", collectionPath);
      // We'll need to find the interface ID and subsection ID at creation time
      return { type: 'subsection', basePath: collectionPath, parentInterfaceSlug, subsectionSlug: pathSegments[pathSegments.length - 1] };
    } else {
      // Regular section - create interface in section collection
      for (let i = 0; i < pathSegments.length; i += 2) {
        collectionPath += `/${pathSegments[i]}.section`;
        if (i + 1 < pathSegments.length) {
          collectionPath += `/${pathSegments[i + 1]}`;
        }
      }
      return { type: 'section', path: collectionPath };
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setInterfaceTitle('');
      setIsCreating(false);
    }
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Create slug from title
  const createSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove symbols except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  const handleCreate = async () => {
    const collectionInfo = buildCollectionPath();
    if (!interfaceTitle.trim() || !institution?.id || !collectionInfo) return;

    setIsCreating(true);

    try {
      const slug = createSlug(interfaceTitle);
      let collectionPath;
      
      if (collectionInfo.type === 'section') {
        collectionPath = collectionInfo.path;
      } else {
        // For subsections, we need to handle differently
        // For now, create in the base path - this might need adjustment based on your data structure
        collectionPath = collectionInfo.basePath;
      }

      // Create document with slug as ID
      const docRef = doc(db, collectionPath, slug);
      
      await setDoc(docRef, {
        title: interfaceTitle.trim(),
        slug: slug,
        type: 'interface',
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'draft',
        components: [], // Empty array for interface components
        sections: [] // Array for nested sections (subsections)
      });

      // Call the callback to refresh the parent component
      if (onInterfaceAdded) {
        onInterfaceAdded();
      }

      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error creating interface:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && interfaceTitle.trim() && !isCreating) {
      handleCreate();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-[#1a1a1a] border border-[#ffffff10] rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium text-white">Create New Interface</h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-[#ffffff10] rounded-lg transition-colors"
            >
              <LuX className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <div className="space-y-4">
            {/* Interface Preview */}
            <div className="flex items-center space-x-4 p-4 bg-[#ffffff05] border border-[#ffffff10] rounded-lg">
              <div className="p-2 rounded-lg bg-purple-400/20">
                <LuFileText className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-white text-base">
                  Interface
                </div>
                <div className="text-sm text-[#ffffff70] mt-0.5">
                  Create a new user interface
                </div>
              </div>
            </div>

            {/* Title Input */}
            <div>
              <label className="block text-sm font-medium text-[#ffffff90] mb-2">
                Interface Title
              </label>
              <input
                type="text"
                value={interfaceTitle}
                onChange={(e) => setInterfaceTitle(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter interface title..."
                className="w-full px-4 py-3 bg-[#ffffff10] border border-[#ffffff20] rounded-lg text-white placeholder-[#ffffff50] focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
                disabled={isCreating}
              />
            </div>

            {/* Create Button */}
            <div className="flex space-x-3 pt-2">
              <button
                onClick={onClose}
                disabled={isCreating}
                className="flex-1 px-4 py-3 bg-[#ffffff10] hover:bg-[#ffffff20] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!interfaceTitle.trim() || isCreating}
                className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create Interface'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewInterfaceModal;